import React from 'react'
import { useTranslation } from 'react-i18next'
import { HelpCircle } from 'lucide-react'

/**
 * Универсальный компонент модального окна справки
 * 
 * @param {boolean} isOpen - Флаг открытия модального окна
 * @param {function} onClose - Функция закрытия модального окна
 * @param {string} title - Заголовок справки
 * @param {Array<string>} steps - Массив текстов шагов
 * @param {boolean} highlightLastStep - Выделить последний шаг зеленым цветом (по умолчанию true)
 * @param {string} buttonText - Текст кнопки закрытия (по умолчанию "Понятно")
 */
const HelpModal = ({ 
  isOpen, 
  onClose, 
  title, 
  steps = [], 
  highlightLastStep = true,
  buttonText,
  htmlContent
}) => {
  const { t } = useTranslation()
  
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm overflow-y-auto">
      <div className="bg-dark-primary rounded-lg w-full max-w-md p-6 my-auto max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 flex-shrink-0">
          <div className="p-2 rounded-lg bg-accent-primary/10">
            <HelpCircle size={24} className="text-accent-primary" />
          </div>
          <h3 className="text-xl font-bold text-white">
            {title}
          </h3>
        </div>

        {/* Steps - с прокруткой */}
        <div className="space-y-3 mb-6 overflow-y-auto flex-1">
          {htmlContent ? (
            <div
              className="text-text-secondary leading-relaxed whitespace-pre-line [&_a]:text-accent-primary [&_a]:underline [&_a]:underline-offset-2 [&_code]:text-accent-primary [&_code]:font-mono [&_code]:bg-white/5 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            steps.map((step, index) => {
              const isLastStep = index === steps.length - 1
              const shouldHighlight = highlightLastStep && isLastStep
              
              // Поддержка как простых строк, так и объектов с title/description
              const isObject = typeof step === 'object' && step !== null
              const title = isObject ? step.title : null
              const description = isObject ? step.description : step
              
              return (
                <div key={index}>
                  {title && (
                    <p className="text-white font-bold mb-1">
                      {title}
                    </p>
                  )}
                  <p 
                    className={`leading-relaxed ${
                      shouldHighlight 
                        ? 'text-accent-primary font-medium' 
                        : 'text-text-secondary'
                    }`}
                  >
                    {description}
                  </p>
                </div>
              )
            })
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="w-full py-3 px-4 rounded-lg bg-accent-primary text-dark-primary font-bold hover:bg-opacity-90 transition-all flex-shrink-0"
        >
          {buttonText || t('common.gotIt', 'Понятно')}
        </button>
      </div>
    </div>
  )
}

export default HelpModal
